import React from "react";
import Marquee from "react-fast-marquee";
import { Heart, ExternalLink } from "lucide-react";

const CharityPartnersSection = ({ isVisible }) => {
  const charities = [
    {
      name: "St. Jude Children's Research Hospital",
      description: "Fighting childhood cancer and pediatric diseases",
      logo: "https://customer-assets.emergentagent.com/job_company-collective/artifacts/fvlnsfhq_image.png",
      url: "https://www.stjude.org/",
    },
    {
      name: "Feeding America",
      description: "Ending hunger in communities nationwide",
      logo: "https://customer-assets.emergentagent.com/job_company-collective/artifacts/jy331dkm_image.png",
      url: "https://www.feedingamerica.org/",
    },
    {
      name: "Wounded Warrior Project",
      description: "Honoring and empowering wounded veterans",
      logo: "https://customer-assets.emergentagent.com/job_company-collective/artifacts/3mfmcox2_image.png",
      url: "https://www.woundedwarriorproject.org/",
    },
    {
      name: "Disabled American Veterans",
      description: "Advocating for veteran benefits and support",
      logo: "https://customer-assets.emergentagent.com/job_company-collective/artifacts/ibyeqfc0_image.png",
      url: "https://www.dav.org/",
    },
  ];

  const trustedBy = [
    { name: "Google", logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" },
    { name: "Amazon", logo: "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" },
    { name: "Microsoft", logo: "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg" },
    { name: "Salesforce", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Salesforce.com_logo.svg/1200px-Salesforce.com_logo.svg.png" },
  ];

  return (
    <section
      id="charities"
      className={`section-fade py-24 sm:py-32 bg-white ${isVisible ? "visible" : ""}`}
      data-testid="charities-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 sm:mb-20">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#0A192F]/70 mb-6">
            <span className="w-8 h-px bg-[#D4AF37]" />
            Our Partners
            <span className="w-8 h-px bg-[#D4AF37]" />
          </span>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#0A192F] tracking-tight font-['Outfit'] mb-6">
            Making a Difference{" "}
            <span className="text-[#D4AF37]">Together</span>
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
            We proudly support respected national organizations making a difference every day.
          </p>
        </div>

        {/* Charity Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {charities.map((charity, index) => (
            <a
              key={charity.name}
              href={charity.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`charity-card group animate-fade-in-up opacity-0 stagger-${index + 1}`}
              data-testid={`charity-${charity.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="h-20 flex items-center justify-center mb-4 grayscale group-hover:grayscale-0 transition-all duration-300">
                <img
                  src={charity.logo}
                  alt={charity.name}
                  className="max-h-16 max-w-full object-contain"
                  loading="lazy"
                />
              </div>
              <h3 className="text-sm font-semibold text-[#0A192F] font-['Outfit'] mb-2 line-clamp-2">
                {charity.name}
              </h3>
              <p className="text-xs text-[#475569] mb-3">
                {charity.description}
              </p>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">
                Visit <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          ))}
        </div>

        {/* Trusted By Section */}
        <div className="border-t border-slate-100 pt-16">
          <p className="text-center text-sm text-[#475569] mb-8 uppercase tracking-widest font-medium">
            Trusted by industry leaders
          </p>
          
          <div className="marquee-container">
            <Marquee gradient={false} speed={40} pauseOnHover>
              {[...trustedBy, ...trustedBy].map((partner, index) => (
                <div
                  key={`${partner.name}-${index}`}
                  className="mx-12 flex items-center justify-center"
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    className="h-8 sm:h-10 partner-logo"
                    loading="lazy"
                  />
                </div>
              ))}
            </Marquee>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CharityPartnersSection;
